import { loginTarget } from '@/lib/apiConfig';
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, ArrowLeft } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import Turnstile, { useTurnstile } from "@/components/Turnstile";
import { nativeRequestHeaders } from "@/lib/native";


export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const turnstile = useTurnstile();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const res = await fetch(`${loginTarget(email).apiUrl}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...nativeRequestHeaders() },
        body: JSON.stringify({ email: email.trim(), ...(turnstile.token && { turnstile_token: turnstile.token }) }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Something went wrong");
        setIsSubmitting(false);
        turnstile.reset();
        return;
      }

      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
      turnstile.reset();
    }
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Check Your Email</h2>
            <p className="text-slate-600 mb-6">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
            </p>
            <a
              href="/login"
              className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to sign in
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full border-2 border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="bg-black rounded-xl px-4 py-2">
              <BrandLogo alt="Seiko Sports Technologies" className="h-8 w-auto object-contain" />
            </div>
          </div>
          <CardTitle className="text-2xl">Forgot Password</CardTitle>
          <p className="text-slate-600 text-sm mt-1">
            Enter your email and we'll send you a reset link
          </p>
        </CardHeader>
        <CardContent className="p-6 pt-4">
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="username"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <Turnstile turnstile={turnstile} />

            <Button
              type="submit"
              disabled={isSubmitting || turnstile.loading || (turnstile.enabled && !turnstile.token)}
              className="w-full h-11 bg-[rgb(var(--brand-yellow))] hover:bg-[rgb(var(--brand-yellow-hover))] text-black font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Reset Link"
              )}
            </Button>

            <div className="text-center">
              <a
                href="/login"
                className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to sign in
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
